/**
 * The scan-miss rule: a line whose scanned rate matched none of the org's
 * rates (or tied two) must not save at 0% by default.
 *
 * The default-rate fill (InvoiceLineItems) gives a scanned line the ONE rate
 * at the scanned percentage; with none, the line stays at 0% and shows
 * "Scanned: N%". Saving it like that would post a purchase with no VAT that
 * the receipt says carried VAT — so the line blocks submit, with an inline
 * error on its tax picker, until the user picks a rate or explicitly picks
 * the 0% option (`tax_rate_confirmed`, set by every pick in the picker). The
 * message names that option as the org's picker lists it — "No VAT" (the
 * synthetic `no_vat`) or the org's own 0% rate (e.g. "BTW 0%"): see
 * {@link zeroRateOptionLabel} — and, on a VAT purchase picker, the
 * "VAT not reclaimable" option for VAT that was charged but cannot be
 * deducted (e.g. foreign VAT).
 *
 * Form-only: `tax_rate_hint` and `tax_rate_confirmed` are stripped by every
 * submit whitelist. One predicate, used by the line schemas' refinement and by
 * the picker (which shows a blank value, not "No VAT", while it holds).
 */
import type { z } from 'zod';
import type { TaxRate } from '@/hooks/useJurisdictionTaxConfig';

/**
 * The zod issue message (an i18n key in the `common` namespace). It takes a
 * `{{zeroOption}}` interpolation: the label of the line's 0% option, and
 * `{{notReclaimableOption}}`: the "VAT not reclaimable" label. A picker
 * without that option (sales tax) renders {@link SCAN_RATE_REQUIRED_ZERO_ONLY}
 * instead, which names the 0% option alone.
 */
export const SCAN_RATE_REQUIRED = 'tax.ui.scanRateRequired';
export const SCAN_RATE_REQUIRED_ZERO_ONLY = 'tax.ui.scanRateRequiredZeroOnly';

export interface ScanMissLine {
  tax_rate?: unknown;
  tax_rate_id?: string | null;
  tax_rate_hint?: number;
  tax_rate_confirmed?: boolean;
}

/** A scanned line still at the fill's 0% with no rate and no explicit pick. */
export function isUnresolvedScanMiss(line: ScanMissLine | null | undefined): boolean {
  if (!line) return false;
  return (line.tax_rate_hint ?? 0) > 0
    && line.tax_rate === 0
    && !line.tax_rate_id
    && line.tax_rate_confirmed !== true;
}

/** `.superRefine` for a line-item schema: the issue lands on `tax_rate`. */
export function refineScanMiss(line: ScanMissLine, ctx: z.RefinementCtx): void {
  if (isUnresolvedScanMiss(line)) {
    ctx.addIssue({ code: 'custom', path: ['tax_rate'], message: SCAN_RATE_REQUIRED });
  }
}

/**
 * The label of the 0% option a picker offers — the org's active in-window DB
 * 0% rate, or the synthetic "No VAT" / "No tax" — never "Exempt" and never
 * "VAT not reclaimable" (VAT WAS charged there; the message names it
 * separately, see {@link notReclaimableOptionLabel}). Undefined when the list
 * has none (the caller falls back to `tax.ui.noVatPurchase`).
 */
export function zeroRateOptionLabel(rates: readonly TaxRate[]): string | undefined {
  return rates.find(
    (r) =>
      r.numericValue === 0
      && r.key !== 'exempt'
      && r.key !== 'non_reclaimable'
      && !r.isInactive
      && !r.otherDate,
  )?.label;
}

/**
 * The label of the picker's "VAT not reclaimable" option, or undefined when
 * the picker has none (a sales-tax org, a sales line). The scan-miss message
 * names it only when it is there to pick.
 */
export function notReclaimableOptionLabel(rates: readonly TaxRate[]): string | undefined {
  return rates.find((r) => r.key === 'non_reclaimable')?.label;
}

/** The line fields the "VAT not reclaimable" gross-up reads and writes (all form-only but the price). */
export interface NotReclaimableGrossUpLine {
  quantity?: number;
  unit_price?: number;
  tax_inclusive?: boolean;
  tax_rate_hint?: number;
  /** The VAT amount the scan printed for the line. */
  tax_rate_hint_vat?: number;
  /** The net line total (quantity × unit price, in cents) that printed VAT was charged on. */
  tax_rate_hint_vat_base?: number;
  /** Set while the line is grossed up: its net unit price. */
  tax_rate_hint_net?: number;
  /** Set while the line is grossed up: the unit price the gross-up WROTE. */
  tax_rate_hint_gross?: number;
  /**
   * The user changed a grossed-up amount and then left "VAT not reclaimable":
   * their figure stands, so a later pick never grosses it up again.
   */
  tax_rate_hint_amount_kept?: boolean;
}

/** The fields one pick changes; `unit_price` absent = the price stays. */
export type NotReclaimableAmountChange = Partial<
  Pick<NotReclaimableGrossUpLine, 'unit_price' | 'tax_rate_hint_vat' | 'tax_rate_hint_net' | 'tax_rate_hint_gross' | 'tax_rate_hint_amount_kept'>
>;

/**
 * The amount fields a change of the line's rate to `newRate` moves, whatever
 * made the change (the picker, an account's default rate, a new custom rate):
 * "VAT not reclaimable" grosses a scanned line up, anything else restores a
 * grossed-up line's net. Null when nothing moves.
 */
export function amountChangeForRate(
  line: NotReclaimableGrossUpLine | null | undefined,
  newRate: unknown,
): NotReclaimableAmountChange | null {
  return newRate === 'non_reclaimable'
    ? grossUpForNotReclaimable(line)
    : restoreNetAfterNotReclaimable(line);
}

const UNIT_PRICE_PRECISION = 1_000_000;
const roundCents = (value: number) => Math.round(value * 100) / 100;
const roundUnit = (value: number) => Math.round(value * UNIT_PRICE_PRECISION) / UNIT_PRICE_PRECISION;

/**
 * The VAT to add to a net line total: the printed VAT amount
 * (`tax_rate_hint_vat`, exact — it reproduces the receipt total) only while
 * the line is still the one scanned — its net total equals the total the VAT
 * was printed on (`tax_rate_hint_vat_base`). Once the user changed the
 * quantity or price, the printed figure belongs to another amount: net × hint
 * rounded to cents. On a multi-line receipt without per-line VAT amounts each
 * line's VAT is rounded on its own, so their sum can differ from the receipt
 * total by a cent (accepted).
 */
function scannedVat(netTotal: number, line: NotReclaimableGrossUpLine): number {
  const printed = line.tax_rate_hint_vat ?? 0;
  if (printed > 0 && line.tax_rate_hint_vat_base === netTotal) return printed;
  return roundCents(netTotal * (line.tax_rate_hint ?? 0) / 100);
}

/** The net line total a printed VAT amount belongs to — stamped beside it by the scan mapper. */
export function scannedVatBase(quantity: number | undefined, unitPrice: number | undefined): number {
  return roundCents((unitPrice ?? 0) * (quantity || 1));
}

/**
 * Picking "VAT not reclaimable" on a SCANNED line. A scanned line holds the
 * receipt's NET amount (the VAT was to go on its own leg); "VAT not
 * reclaimable" books the line's amount as cost with no VAT leg, so left as is
 * the VAT paid would vanish from the books. The line is therefore grossed up
 * to net + the scanned VAT, spread over the quantity at 6 decimals (so the
 * line total lands on the intended cents), and both the net and the price
 * written are remembered so a pick of a real rate can restore the net.
 * Null — nothing changes — for a manual line (no scan hint: the user entered
 * what they paid), a tax-inclusive line (already gross), a line already
 * grossed up (never twice), or a line whose amount the user stated after an
 * earlier gross-up (`tax_rate_hint_amount_kept`).
 */
export function grossUpForNotReclaimable(
  line: NotReclaimableGrossUpLine | null | undefined,
): NotReclaimableAmountChange | null {
  if (!line || !((line.tax_rate_hint ?? 0) > 0) || line.tax_inclusive) return null;
  if (line.tax_rate_hint_net !== undefined || line.tax_rate_hint_amount_kept) return null;
  const net = line.unit_price ?? 0;
  const quantity = line.quantity || 1;
  const netTotal = scannedVatBase(quantity, net);
  const gross = roundUnit((netTotal + scannedVat(netTotal, line)) / quantity);
  return { unit_price: gross, tax_rate_hint_net: net, tax_rate_hint_gross: gross };
}

/**
 * Leaving "VAT not reclaimable" for any other pick. A grossed-up line gets its
 * net back only while its price is still exactly the one the gross-up WROTE
 * (never recomputed — a quantity edit since must not break the match). If the
 * user changed the price since, their figure stands: the printed VAT amount
 * is dropped and the line is marked so a later "VAT not reclaimable" pick
 * never grosses it up again. Either way the markers clear. Null when the line
 * was never grossed up.
 */
export function restoreNetAfterNotReclaimable(
  line: NotReclaimableGrossUpLine | null | undefined,
): NotReclaimableAmountChange | null {
  if (!line || line.tax_rate_hint_net === undefined) return null;
  const cleared = { tax_rate_hint_net: undefined, tax_rate_hint_gross: undefined };
  return line.unit_price === line.tax_rate_hint_gross
    ? { ...cleared, unit_price: line.tax_rate_hint_net }
    : { ...cleared, tax_rate_hint_vat: undefined, tax_rate_hint_amount_kept: true };
}
